import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  IdentificationIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { engineerService, EngineerMe } from '../../services/engineerService';
import { PageHeader, Button, FormField, TextInput, Badge } from '../../components/ui';

function daysUntil(iso?: string | null): number | null {
  if (!iso) return null;
  try {
    const now = new Date();
    const d = new Date(iso);
    return Math.ceil((d.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  } catch {
    return null;
  }
}

function expiryBadge(iso?: string | null) {
  const days = daysUntil(iso);
  if (days === null) return null;
  if (days < 0) return <Badge tone="danger">Expired</Badge>;
  if (days <= 30) return <Badge tone="warning">Expires in {days}d</Badge>;
  return <Badge tone="success">Valid · {days}d left</Badge>;
}

const EngineerDocuments: React.FC = () => {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<EngineerMe>({
    queryKey: ['engineer', 'me'],
    queryFn: () => engineerService.me(),
  });

  const [gasNumber, setGasNumber] = useState('');
  const [gasExpiry, setGasExpiry] = useState('');
  const [quals, setQuals] = useState<string[]>([]);
  const [skills, setSkills] = useState<string[]>([]);
  const [newQual, setNewQual] = useState('');
  const [newSkill, setNewSkill] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!data) return;
    setGasNumber(data.gasSafeNumber || '');
    setGasExpiry(data.gasSafeExpiry ? data.gasSafeExpiry.slice(0, 10) : '');
    setQuals(data.qualifications || []);
    setSkills(data.skills || []);
  }, [data]);

  const dirty = useMemo(() => {
    if (!data) return false;
    return (
      (data.gasSafeNumber || '') !== gasNumber ||
      (data.gasSafeExpiry ? data.gasSafeExpiry.slice(0, 10) : '') !== gasExpiry ||
      JSON.stringify(data.qualifications || []) !== JSON.stringify(quals) ||
      JSON.stringify(data.skills || []) !== JSON.stringify(skills)
    );
  }, [data, gasNumber, gasExpiry, quals, skills]);

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await engineerService.updateMe({
        gasSafeNumber: gasNumber.trim() || null,
        gasSafeExpiry: gasExpiry ? new Date(gasExpiry).toISOString() : null,
        qualifications: quals,
        skills,
      });
      setMessage('Saved');
      qc.invalidateQueries({ queryKey: ['engineer', 'me'] });
    } catch (err: any) {
      setMessage(err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const gasDays = daysUntil(gasExpiry);

  return (
    <div className="max-w-3xl mx-auto px-4 py-4 space-y-6">
      <PageHeader
        title="Documents & qualifications"
        subtitle="Keep your Gas Safe registration, qualifications and skills up to date."
      />

      {isLoading && <div className="text-sm text-slate-500">Loading…</div>}

      <section className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex items-center gap-2">
          <IdentificationIcon className="w-5 h-5 text-slate-500" />
          <h2 className="text-sm font-semibold text-slate-900">Gas Safe registration</h2>
          {expiryBadge(gasExpiry)}
        </div>
        <p className="text-xs text-slate-500 mt-1">
          Required for CP12, CP15 and CP17 certificates. You'll get a warning 30 days
          before expiry.
        </p>

        <div className="grid sm:grid-cols-2 gap-3 mt-3">
          <FormField label="Gas Safe number">
            <TextInput
              placeholder="e.g. 000123"
              value={gasNumber}
              onChange={(e) => setGasNumber(e.target.value)}
            />
          </FormField>
          <FormField label="Expiry date">
            <TextInput
              type="date"
              value={gasExpiry}
              onChange={(e) => setGasExpiry(e.target.value)}
            />
          </FormField>
        </div>

        {gasDays !== null && gasDays < 0 && (
          <div className="mt-3 flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
            <ExclamationTriangleIcon className="w-4 h-4 mt-0.5" />
            Your Gas Safe registration expired {Math.abs(gasDays)} days ago. You won't be
            able to submit gas certificates until you renew.
          </div>
        )}
        {gasDays !== null && gasDays >= 0 && gasDays <= 30 && (
          <div className="mt-3 flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
            <ExclamationTriangleIcon className="w-4 h-4 mt-0.5" />
            Your Gas Safe registration expires in {gasDays} days. Renew soon to avoid
            disruption.
          </div>
        )}
      </section>

      <section className="bg-white border border-slate-200 rounded-xl p-4">
        <h2 className="text-sm font-semibold text-slate-900">Qualifications</h2>
        <p className="text-xs text-slate-500 mt-1">
          E.g. ACS CCN1, CENWAT, 18th Edition, PAT, F-Gas, Water Regs 402. These are
          surfaced to admins when assigning jobs.
        </p>

        <div className="flex flex-wrap gap-2 mt-3">
          {quals.map((q) => (
            <span
              key={q}
              className="inline-flex items-center gap-1 text-xs bg-slate-100 text-slate-800 rounded-full pl-3 pr-1 py-1"
            >
              {q}
              <button
                type="button"
                onClick={() => setQuals(quals.filter((x) => x !== q))}
                className="p-0.5 rounded-full hover:bg-slate-200"
                aria-label="Remove"
              >
                <TrashIcon className="w-3.5 h-3.5" />
              </button>
            </span>
          ))}
          {quals.length === 0 && (
            <div className="text-xs text-slate-500">No qualifications added yet.</div>
          )}
        </div>

        <div className="flex items-end gap-2 mt-3">
          <FormField label="Add qualification" className="flex-1">
            <TextInput
              placeholder="e.g. 18th Edition"
              value={newQual}
              onChange={(e) => setNewQual(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newQual.trim()) {
                  setQuals([...quals, newQual.trim()]);
                  setNewQual('');
                }
              }}
            />
          </FormField>
          <Button
            leftIcon={<PlusIcon className="w-4 h-4" />}
            variant="outline"
            onClick={() => {
              if (newQual.trim()) {
                setQuals([...quals, newQual.trim()]);
                setNewQual('');
              }
            }}
          >
            Add
          </Button>
        </div>
      </section>

      <section className="bg-white border border-slate-200 rounded-xl p-4">
        <h2 className="text-sm font-semibold text-slate-900">Skills</h2>
        <p className="text-xs text-slate-500 mt-1">
          Used for smart job assignment (e.g. "commercial gas", "unvented hot water",
          "three-phase").
        </p>

        <div className="flex flex-wrap gap-2 mt-3">
          {skills.map((s) => (
            <span
              key={s}
              className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-800 rounded-full pl-3 pr-1 py-1"
            >
              {s}
              <button
                type="button"
                onClick={() => setSkills(skills.filter((x) => x !== s))}
                className="p-0.5 rounded-full hover:bg-blue-100"
                aria-label="Remove"
              >
                <TrashIcon className="w-3.5 h-3.5" />
              </button>
            </span>
          ))}
          {skills.length === 0 && (
            <div className="text-xs text-slate-500">No skills added yet.</div>
          )}
        </div>

        <div className="flex items-end gap-2 mt-3">
          <FormField label="Add skill" className="flex-1">
            <TextInput
              placeholder="e.g. Commercial gas"
              value={newSkill}
              onChange={(e) => setNewSkill(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newSkill.trim()) {
                  setSkills([...skills, newSkill.trim()]);
                  setNewSkill('');
                }
              }}
            />
          </FormField>
          <Button
            leftIcon={<PlusIcon className="w-4 h-4" />}
            variant="outline"
            onClick={() => {
              if (newSkill.trim()) {
                setSkills([...skills, newSkill.trim()]);
                setNewSkill('');
              }
            }}
          >
            Add
          </Button>
        </div>
      </section>

      <div className="flex items-center justify-end gap-2">
        {message === 'Saved' && (
          <span className="text-xs text-emerald-700 inline-flex items-center gap-1">
            <CheckCircleIcon className="w-4 h-4" /> Saved
          </span>
        )}
        {message && message !== 'Saved' && (
          <span className="text-xs text-red-700">{message}</span>
        )}
        <Button onClick={save} loading={saving} disabled={!dirty}>
          Save changes
        </Button>
      </div>
    </div>
  );
};

export default EngineerDocuments;
